import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Fetcher } from 'react-router';
import { useFetchers, useSearchParams } from 'react-router';
import type { PAGE_POSITIONS } from 'types';
import type { Image } from 'types/capi/shared';
import type { User } from 'types/User';

import FlameIcon from '~/assets/icons/Flame';
import NotifyMeBell from '~/assets/icons/NotifyMeBell';
import type { StockStatus } from '~/domain/stockStatus';
import ClickNotificationEvent from '~/events/ClickNotificationEvent';
import useCookiesAccepted from '~/hooks/useCookiesAccepted';
import useRegion from '~/hooks/useRegion';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import { formatNumberToK } from '~/utils/numbers';

import Modals from './Modals';
import Tracked from './Tracked';

enum FetchState {
  Idle,
  Setting,
  Unsetting
}

function getFetchState(product: NotificationData, fetchers: Fetcher[]) {
  const url = `/bolt/notification/${product.id}`;
  const fetcher = fetchers.find((f) => f.formAction?.startsWith(url));

  if (!fetcher) return FetchState.Idle;

  return fetcher?.formData?.get('delete')
    ? FetchState.Unsetting
    : FetchState.Setting;
}

function getIsNotified(
  state: FetchState,
  product: NotificationData,
  user: User
) {
  if (state !== FetchState.Idle) return state === FetchState.Setting;

  return Boolean(
    user.actions?.alerts?.products?.some((p) => product.id === Number(p.id))
  );
}

export type NotificationData = {
  affiliateName?: string;
  id: number;
  image?: Image;
  legacyId: string;
  name: string;
  price: number | null;
  queryID?: string;
  stockStatus?: StockStatus;
  weeklyNotificationsCount?: number;
};

type MainProps = {
  buttonStyle: 'compact' | 'normal';
  className?: string;
  mode?: 'light' | 'dark';
  openModalViaQueryString?: boolean;
  pagePosition: PAGE_POSITIONS | `TEMP_${string}`;
  product: NotificationData;
  showTooltip?: boolean;
  variant?: 'none';
};

const ProductNotificationButton = ({
  product,
  buttonStyle,
  className,
  pagePosition,
  mode = 'light',
  showTooltip = false,
  openModalViaQueryString = false
}: MainProps) => {
  const user = useUser();
  const fetchers = useFetchers();
  const [searchParams, setSearchParams] = useSearchParams();
  const cookiesAccepted = useCookiesAccepted();

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const shouldOpenFromQueryString =
      openModalViaQueryString &&
      cookiesAccepted &&
      (searchParams.has('modal') ||
        searchParams.get('notify') === product.id.toString());

    if (shouldOpenFromQueryString) {
      setIsModalOpen(true);
    }
  }, [openModalViaQueryString, cookiesAccepted, searchParams, product.id]);

  const fetchState = getFetchState(product, fetchers);
  const isNotified = getIsNotified(fetchState, product, user);
  const { isBot, clientOs } = useRootData();

  const region = useRegion();

  function onClick(e: React.SyntheticEvent) {
    e.preventDefault();
    e.stopPropagation();

    setIsModalOpen(true);

    return new ClickNotificationEvent({
      buttonName: 'OPEN_NOTIFICATION',
      isBot,
      legacyProductId: product.legacyId,
      os: clientOs,
      pagePosition: pagePosition,
      productId: product.id,
      productName: product.name,
      queryID: product.queryID ?? localStorage.getItem('queryID'),
      user
    }).send();
  }

  function onModalDismiss() {
    setIsModalOpen(false);

    if (searchParams.has('modal') || searchParams.has('notify')) {
      setSearchParams((params) => {
        params.delete('modal');
        params.delete('notify');

        return params;
      });
    }
  }

  if (region.code !== 'GB') return null;

  return (
    <div className={className}>
      {buttonStyle === 'compact' ? (
        <CompactButton
          isNotified={isNotified}
          mode={mode}
          isDisabled={fetchState !== FetchState.Idle}
          onClick={onClick}
          className={className}
          pagePosition={pagePosition}
        />
      ) : (
        <NormalButton
          isNotified={isNotified}
          showTooltip={showTooltip}
          onClick={onClick}
          mode={mode}
          isDisabled={fetchState !== FetchState.Idle}
          product={product}
          className={className}
          pagePosition={pagePosition}
        />
      )}
      <Modals.ProductNotification
        product={product}
        isOpen={isModalOpen}
        onDismiss={onModalDismiss}
      />
    </div>
  );
};

type ButtonProps = {
  className?: string;
  isDisabled: boolean;
  isNotified: boolean;
  mode: 'light' | 'dark';
  pagePosition: MainProps['pagePosition'];
  showTooltip?: boolean;
} & React.HTMLProps<HTMLButtonElement>;

const CompactButton = ({
  onClick,
  mode,
  pagePosition,
  isDisabled,
  isNotified
}: ButtonProps) => {
  return (
    <Tracked.Button
      className="transition-transform duration-200 ease-in-out hover:scale-125"
      onClick={onClick}
      disabled={isDisabled}
      type="submit"
      data-cy="product-notification-button-compact"
      name="OPEN_NOTIFICATION"
      pagePosition={pagePosition}
      variant="none"
    >
      <NotifyMeBell mode={mode} variant={isNotified ? 'filled' : 'thin'} />
    </Tracked.Button>
  );
};

const NormalButton = ({
  product,
  isNotified,
  showTooltip,
  mode,
  isDisabled,
  onClick,
  pagePosition,
  className
}: ButtonProps & { product: NotificationData }) => {
  return (
    <Tracked.Button
      type="submit"
      className="flex w-full overflow-hidden"
      onClick={onClick}
      disabled={isDisabled}
      data-cy="product-notification-button-normal"
      name="OPEN_NOTIFICATION"
      pagePosition={pagePosition}
      variant="none"
    >
      {showTooltip && !isNotified && <Tooltip product={product} />}
      <div className={getNormalButtonStyles(isNotified, mode, className)}>
        <NotifyMeBell mode={mode} variant={isNotified ? 'filled' : 'thick'} />
        <span className="flex items-center font-semibold tracking-wide">
          {isNotified ? 'Notified' : 'Notify me'}
        </span>
      </div>
    </Tracked.Button>
  );
};

const Tooltip = ({ product }: { product: NotificationData }) => {
  const { t } = useTranslation();

  const { stockStatus, weeklyNotificationsCount = 0 } = product;

  return (
    <div className="z-10 -mr-2 flex animate-slide-from-right items-center self-stretch whitespace-nowrap bg-purple-hola/5 pl-2 pr-4 text-purple-hola opacity-0">
      {weeklyNotificationsCount >= 50 ? (
        <div className="flex gap-1 whitespace-nowrap">
          <FlameIcon width={16} height={16} />
          {formatNumberToK(weeklyNotificationsCount)}
          <span className="hidden sm:block">notifications</span> set in the last
          7 days
        </div>
      ) : (
        <>
          {['in-stock', 'reseller'].includes(stockStatus!) &&
            t('getPriceDropAlert')}
          {['coming-soon', 'tbc'].includes(stockStatus!) &&
            t('getReleaseAlert')}
          {stockStatus === 'sold-out' && 'Get a restock alert'}
        </>
      )}
    </div>
  );
};

function getNormalButtonStyles(
  isNotified: boolean,
  mode: 'dark' | 'light',
  className: string = ''
) {
  return `flex w-full items-center justify-center gap-2 rounded-lg p-2 transition duration-200 ease-in-out z-20 ${className} ${
    isNotified
      ? `border  ${mode === 'dark' ? 'text-purple-hola-light border-purple-hola-light ' : 'text-purple-hola border-purple-hola'}`
      : `${mode === 'dark' ? 'text-black bg-purple-hola-light' : 'text-white bg-purple-hola '}`
  }`;
}

export default ProductNotificationButton;
